/**
 * Roles of a member (docs/01-architektur.md, Schnittstelle), as in KellerLog but without storages:
 * what someone may do in a business. The server enforces it; the app only hides what is not allowed.
 */

export type Role = "owner" | "manager" | "staff" | "viewer";
export const ROLES: readonly Role[] = ["owner", "manager", "staff", "viewer"];
export const ROLE_LABELS: Record<Role, string> = { owner: "Inhaber", manager: "Verwaltung", staff: "Mitarbeit", viewer: "Nur sehen" };

/** What each role may, as the owner reads it when choosing. */
export const ROLE_HINTS: Record<Role, string> = {
  owner: "alles, auch Team und Anbindung an KellerLog",
  manager: "Speisen und Allergene pflegen, Speisekarten erstellen und veröffentlichen",
  staff: "Speisekarten erstellen, ändern und drucken; Speisen nur ansehen",
  viewer: "Speisekarten und Speisen ansehen und drucken",
};

/** Dishes, allergens, the spreadsheet, the settings of the card. */
export const editsCatalog = (role: Role) => role === "owner" || role === "manager";
/** Create, change and delete menus. */
export const editsMenus = (role: Role) => role !== "viewer";
/** Mark a dish „ausverkauft“ on the menu on the net (S39): everyone who makes menus, the service too. */
export const marksSoldOut = (role: Role) => role !== "viewer";
/** Publish a menu on the net and withdraw it; the link and its QR code. */
export const publishes = (role: Role) => role === "owner" || role === "manager";
/** The team and the connection to KellerLog. */
export const ownsBusiness = (role: Role) => role === "owner";

/**
 * What the app keeps of a business on the device has this shape (S17). Whenever the server's
 * `/api/data` gets another field, DATA_FORMAT goes up and the field is named here: an app with data
 * of the old shape then loads all of it again instead of breaking on what it lacks (store.ts). A
 * test of the server holds the two together. The same when a dish gets another field: 3 brought the
 * price label and the further prices (S19); 4 the Word templates per style and the tags' symbols (S21);
 * 5 the drinks with their categories and tags (S22); 6 the dishes sold out today (S39); 7 the dishes' translations and the business's languages (S36); 8 the corrections of „Deutsch prüfen“ (S47); 9 the own pages (S50); 10 the PDF slots (S54); 11 the styles' version (S55); 12 the business's dictionary of its own words (S36); 13 a PDF slot's own address by a redirect (S40); 14 what a PDF slot shows, its mark and language (S62, Z-0); 15 the folders of the weekly plan and their PDFs (S62, Stufe 2).
 */
export const DATA_FORMAT = 15;
export const DATA_FIELDS = ["version", "workspace", "role", "dishes", "allergens", "categories", "tags", "texts", "menus", "settings", "published", "word", "designs", "designsVersion", "logo", "tagIcons", "drinkCategories", "drinkTags", "soldOut", "proofs", "ownPages", "pdfSlots", "phrases", "folders", "folderPdfs"] as const;
